import { Icon } from "@iconify/react";
import { Button, Col, Form, Input, Row, Space, Typography, UploadProps, Select, message } from "antd";
import { useState, ReactNode, useCallback, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper, DataTable, WrapperCard, PDFViewer, PDFView } from "~/Components";
import styles from "../../ContractManagement/SPA/SPA.module.scss";
import { ErrorMessage, Field, FieldProps, Formik, FormikTouched, useFormik } from "formik";
import { useAuth } from "~/Hooks/useAuth";
import { filter, map, some, uniqueId } from "lodash";
import { useNotification } from "~/Hooks/useNotification";
import moment from "moment";
import { useQuery } from "~/Hooks/useQuery";
import { get, post, put } from "@services/api.service";
import { Subsidiary } from "../Subsidiaries/Subsidiaries";
import { allowedDomainsAll } from "~/Utils";

interface FormItems {
  watcher: {
    designation: any;
    userName: string;
    userEmail: string;
    subsidiary: string;
    division: string;
    authorityGroup: string;
  }[];
}

const defaultValue = {
  watcher: [
    {
      userName: "",
      userEmail: "",
      designation: "R1",
      subsidiary: "",
      division: "",
      authorityGroup: "",
    },
  ],
} as FormItems;

var newDocId = 100;

export const AddWatchers = () => {
  const [docsList, setDocsList] = useState<any>([]);
  const [record, setRecord] = useState<any>({});
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { openNotification } = useNotification();
  const [isloading, setIsLoading] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [initialValues, setInitialValues] = useState<FormItems>(defaultValue);
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [divisionData, setDivisionData] = useState([]);
  const [subsidiaryData, setSubsidiaryData] = useState([]);
  const [subsidiaryId, setSubsidiaryId] = useState<any>();
  const [divisionName, setDivisionName] = useState<any>([]);
  const [subsidiaryName, setSubsidiaryName] = useState([]);
  const [approverData, setApproverData] = useState([]);
  const [deletedIds, setDeletedIds] = useState<any>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [filter, setFilter] = useState<any>();
  const query = useQuery();
  const authority = query?.get("activeTab") || "";
  const id = query?.get("id") || "";
  const viewItem = query?.get("view") || "";

  useEffect(() => {
    get(`v1/spa/watcher/watcher-detail?uniqueId=${id}`)
      .then((res: any) => {
        const values = {
          watcher: [
            {
              userName: res.response.data.userName,
              userEmail: res.response.data.userEmail,
              designation: res.response.data.designation,
              subsidiary: res.response.data.subsidiary,
              division: res.response.data.division,
              authorityGroup: res.response.data.authorityGroup,
            },
          ],
        } as FormItems;
        setInitialValues(values);
        setFilter(res.response.data.subsidiary);
      })
      .catch(() => {});
  }, []);

  const onSubmit = async (values: any) => {
    let watcher: any = [...values.watcher];

    watcher = watcher.map((item: any) => ({
      ...item,
      authorityGroup: authority,
      subsidiary: item.subsidiary.split("/")[1] ? item.subsidiary.split("/")[1] : item.subsidiary,
    }));
    const payload: any = {
      watcher: [...watcher],
    };
    const updateWatcher = watcher.map((item: any) => {
      return { ...item, uniqueId: id };
    });
    updateWatcher[0];
    setSubmitLoading(true);
    let updateUrl = id ? `update-watcher` : `create-watcher`;
    if (id) {
      put(`v1/spa/watcher/${updateUrl}`, updateWatcher[0])
        .then((res: any) => {
          if (res) {
            openNotification({
              title: "Success",
              message: `Watcher ${id ? "updated" : "added"}  successfully`,
              type: "success",
            });
          }
          setSubmitLoading(false);
          navigate(`/watchers?activeTab=${authority}`);
        })
        .catch((err) => {
          openNotification({
            title: err,
            message: "",
            type: "error",
          });
          setSubmitLoading(false);
        });
    } else {
      post(`v1/spa/watcher/${updateUrl}`, watcher)
        .then((res: any) => {
          if (res) {
            openNotification({
              title: "Success",
              message: `Watcher added successfully`,
              type: "success",
            });
          }
          setSubmitLoading(false);
          navigate(`/watchers?activeTab=${authority}`);
        })
        .catch((err) => {
          setSubmitLoading(false);
        });
    }
  };

  const getUserRecords = () => {
    const subsidiaryUrl = `v1/spa/watcher/get-active-user-list?authorityGroup=${authority}&subsidiary=${subsidiaryName}&division=${encodeURIComponent(divisionName)}&designation=R1`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setApproverData(responseData);
          } else {
            console.error("No records found in the response");
            setApproverData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Approvers failed",
          type: "error",
        });
      });
  };

  useEffect(() => {
    if (divisionName?.length > 0) {
      getUserRecords();
    }
  }, []);

  const validationSchema2 = yup.object().shape({
    watcher: yup.array().of(
      yup.object().shape({
        userName: yup
          .string()
          .trim("Name cannot include leading and trailing spaces")
          .strict(true)
          .required("Name required")
          .matches(/^[aA-zZ\s]+$/, "Invalid Name"),
        userEmail: yup
          .string()
          .lowercase("Email address must be lowercase")
          .email("Email address must be a valid userEmail")
          .strict(true)
          .required("Email required"),
        designation: yup.string().required("Designation is required"),
        subsidiary: yup.string().required("Subsidiary/Division is required"),
        division: yup.string().required("Cost Centre is required"),
      })
    ),
  });

  const getSubsidiaryRecords = (subsidiaryId?: any) => {
    const subsidiaryUrl = `/v1/subsidiary/all-subsidiary`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (id && res && res.response && res.response.data && filter) {
          const filteredSubsidiary = res.response.data.find((item: any) => item.name == filter);
          if (filteredSubsidiary) {
            const filterSubsidiaryId = filteredSubsidiary.subsidiaryId;
            setSubsidiaryId(filterSubsidiaryId);
          } else {
            console.log("No matching subsidiary found for the given name.");
          }
          const responseData = res.response.data;
          if (responseData) {
            setSubsidiaryData(responseData);
          } else {
            console.error("No records found in the response");
            setSubsidiaryData([]);
          }
        } else if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          setSubsidiaryData(responseData);
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Subsidiary failed",
          type: "error",
        });
      });

    if (subsidiaryId) {
      const divisionUrl = `/v1/division/get-all-divisionBySubsidiary?subsidiary=${subsidiaryId}`;
      get(divisionUrl)
        .then((res: any) => {
          if (res && res.response && res.response.data) {
            const responseData = res.response.data;
            if (responseData) {
              setDivisionData(responseData);
            } else {
              console.error("No records found in the response");
              setDivisionData([]);
            }
          } else {
            console.error("Unexpected response structure:", res);
          }
        })
        .catch((err) => {
          console.error("API Error:", err);
          openToast({
            content: "Getting Division failed",
            type: "error",
          });
        });
    }
  };

  const [checkMail, setCheckMail] = useState<any>();

  useEffect(() => {
    if (checkMail) {
      get(`v1/user/is-valid-email?userEmail=${checkMail}`)
        .then((res: any) => {
          if (res) {
            openNotification({
              message: "User is present in Organization",
              type: "success",
            });
          }
        })
        .catch(() => {
          openToast({
            content: "User not present in Organization",
            type: "error",
          });
        });
    }
  }, [checkMail]);

  useEffect(() => {
    if (id) {
      getSubsidiaryRecords(subsidiaryId);
    } else {
      getSubsidiaryRecords();
    }
  }, [id, filter, subsidiaryId]);

  return (
    <>
      <ContentWrapper
        loading={isloading}
        customTitle={
          <Space align="start">
            <Icon
              onClick={() => navigate(-1)}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles.primary,
                marginRight: styles.whitespace1,
              }}
            />
            <span>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
              >
                {viewItem ? "View" : id ? "Edit" : "Add"} Watcher
              </Typography.Title>
            </span>
          </Space>
        }
      >
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => onSubmit(values)}
          validationSchema={validationSchema2}
        >
          {(props) => {
            const { values, setFieldValue, setFieldTouched, setErrors } = props;

            useEffect(() => {
              if (initialValues.watcher.length > 0) {
                const initialSelectedNames = initialValues.watcher.map((watcher) => watcher.userName);
                setSelectedNames(initialSelectedNames);
              }
            }, [initialValues]);

            useEffect(() => {
              initialValues.watcher.forEach((watcher: any, index: any) => {
                if (watcher) {
                  setFieldValue(`watcher[${index}].userEmail`, watcher.userEmail);
                  setFieldValue(`watcher[${index}].userName`, watcher.userName);
                  setFieldValue(`watcher[${index}].designation`, watcher.designation);
                  setFieldValue(`watcher[${index}].subsidiary`, watcher.subsidiary);
                  setFieldValue(`watcher[${index}].division`, watcher.division);
                  setSubsidiaryName(watcher.subsidiary);
                }
              });
            }, [initialValues]);

            useEffect(() => {
              if (id) {
                let index = initialValues.watcher.length - 1;
                setTimeout(() => {
                  setFieldTouched(`watcher[${index}].userEmail`, false);
                  setFieldTouched(`watcher[${index}].userName`, false);
                  setFieldTouched(`watcher[${index}].designation`, false);
                  setFieldTouched(`watcher[${index}].subsidiary`, false);
                  setFieldTouched(`watcher[${index}].division`, false);
                }, 0);
              }
            }, [id, initialValues, props.values]);

            useEffect(() => {
              setErrors({});
            }, [subsidiaryId]);

            if (!id) {
              props.values.watcher.forEach((watcher: any) => {
                if (watcher.subsidiary) {
                  const subsidiaryName = watcher.subsidiary.split("/")[1];
                  setSubsidiaryName(subsidiaryName);
                  const subsidiaryId = watcher.subsidiary.split("/")[0];
                }
              });
            }

            // const getReviewerCount = (role: any) => {
            //   switch (role) {
            //     case "MD/GCEO":
            //       return 4;
            //     case "GCFO/GCOO":
            //       return 3;
            //     case "HeadofSubsidiary":
            //       return 2;
            //       case "AIRPORTMANAGER":
            //         return 5;
            //     default:
            //       return 0;y
            //   }
            // };

            // const getDesignations = (count: any) => {
            //   switch (count) {
            //     case 4:
            //       return ["R2", "R3", "GCEO", "MD"];
            //     case 3:
            //       return ["R2", "GCFO", "GCOO"];
            //     case 2:
            //       return ["R2", "HEAD OF SUBSIDIARY"];
            //       case 5:
            //         return ["R1", "R2", "Airport Manager"];
            //     default:
            //       return [];
            //   }
            // };

            // const reviewerCount = getReviewerCount(authority.toLocaleUpperCase());
            // const designations = getDesignations(reviewerCount);

            return (
              <Form
                layout="vertical"
                onFinish={props.handleSubmit}
              >
                <div style={{ marginTop: 16 }}>
                  <div>
                    {
                      <>
                        <div
                          style={{
                            width: "100%",
                            marginBottom: 16,
                          }}
                        >
                          <Typography.Title
                            level={4}
                            style={{ marginBottom: "25px" }}
                          >
                            Watcher Details (
                            {authority === "headOfSubsidiary"
                              ? "Head of Subsidiary"
                              : authority === "airportManager"
                              ? "Airport Manager"
                              : authority.toUpperCase()}
                            )
                          </Typography.Title>
                          <div>
                            {props.values.watcher.map((signer, i) => (
                              <Row
                                gutter={[16, 16]}
                                align="top"
                                key={`singer${i}`}
                              >
                                <Col span={6}>
                                  <Form.Item label="Designation">
                                    <Field name={`watcher[${i}].designation`}>
                                      {({ field }: FieldProps) => (
                                        <Input
                                          readOnly={true}
                                          {...field}
                                        />
                                      )}
                                    </Field>
                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`watcher[${i}].designation`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="Subsidiary/Division">
                                    <Field name={`watcher[${i}].subsidiary`}>
                                      {({ field, form }: FieldProps) => (
                                        <Select
                                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                          className={viewItem ? "viewItem" : "none"}
                                          disabled={viewItem ? true : false}
                                          showSearch
                                          value={field.value}
                                          filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                          }
                                          onBlur={() => form.setFieldTouched(`watcher[${i}].subsidiary`, true)}
                                          onChange={(value) => {
                                            const [id, name] = value.split("/");
                                            setSubsidiaryName(name);
                                            form.setFieldValue(`watcher[${i}].subsidiary`, value);
                                            form.setFieldValue(`watcher[${i}].division`, "");
                                            getSubsidiaryRecords(id);
                                          }}
                                          // status={
                                          //   form.touched.watcher?.[i]?.subsidiary && form.errors.watcher?.[i]?.subsidiary ? "error" : ""
                                          // }
                                        >
                                          {subsidiaryData.map((values: any) => (
                                            <Select.Option
                                              value={`${values.subsidiaryId}/${values.name}`}
                                              key={values.subsidiaryId}
                                            >
                                              {values.name}
                                            </Select.Option>
                                          ))}
                                        </Select>
                                      )}
                                    </Field>

                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`watcher[${i}].subsidiary`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="Cost Centre">
                                    <Field name={`watcher[${i}].division`}>
                                      {({ field, form }: FieldProps) => (
                                        <Select
                                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                          className={viewItem ? "viewItem" : "none"}
                                          disabled={viewItem ? true : props.values.watcher[i].subsidiary ? false : true}
                                          showSearch
                                          value={field.value}
                                          filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                          }
                                          // onBlur={() => props.setFieldTouched("subsidiary", true)}
                                          onChange={(value) => {
                                            const [id, name] = value.split("/");
                                            setDivisionName(value);
                                            form.setFieldValue(`watcher[${i}].division`, value);
                                          }}
                                          // status={(props.touched.watcher && props.errors.watcher && "error") || ""}
                                        >
                                          {divisionData &&
                                            divisionData.map((item: any) => (
                                              <Select.Option
                                                value={item.division}
                                                key={item.uniqueId}
                                              >
                                                {item.division}
                                              </Select.Option>
                                            ))}
                                        </Select>
                                      )}
                                    </Field>
                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`watcher[${i}].division`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="User Name">
                                    <Field name={`watcher[${i}].userName`}>
                                      {({ field, form }: FieldProps) => (
                                        // <Select
                                        //   className={viewItem ? "viewItem" : "none"}
                                        //   disabled={viewItem ? true : false}
                                        //   showSearch
                                        //   value={field.value}
                                        //   placeholder="Select name"
                                        //   onChange={(value, option: any) => {
                                        //     form.setFieldValue(`watcher[${i}].userName`, value);
                                        //     // form.setFieldValue(`watcher[${i}].userEmail`, option.label);

                                        //     // Update the selected names
                                        //     const updatedNames = [...selectedNames];
                                        //     if (field.value) {
                                        //       const index = updatedNames.indexOf(field.value);
                                        //       if (index > -1) updatedNames.splice(index, 1);
                                        //     }
                                        //     updatedNames.push(value);
                                        //     setSelectedNames(updatedNames);
                                        //   }}
                                        //   filterOption={(input, option: any) =>
                                        //     option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        //   }
                                        // >
                                        //   {approverData
                                        //     .filter((user: any) => !selectedNames.includes(user.name))
                                        //     .map((user: any, index: any) => (
                                        //       <Select.Option
                                        //         key={index}
                                        //         value={user.name}
                                        //         label={user.email}
                                        //       >
                                        //         {user.name}
                                        //       </Select.Option>
                                        //     ))}
                                        // </Select>
                                        <Input
                                          disabled={viewItem ? true : false}
                                          {...field}
                                          style={{ width: "100%" }}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            form.setFieldValue(`watcher[${i}].userName`, value);
                                          }}
                                        />
                                      )}
                                    </Field>

                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`watcher[${i}].userName`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item label="User Email ID">
                                    <Field name={`watcher[${i}].userEmail`}>
                                      {({ field, form }: FieldProps) => (
                                        // <Select
                                        //   disabled={viewItem ? true : false}
                                        //   showSearch
                                        //   {...field}
                                        //   style={{ width: "100%" }}
                                        //   placeholder="Select an email"
                                        //   onChange={(value, option: any) => {
                                        //     const selectedName = option ? option.label : "";
                                        //     form.setFieldValue(`watcher[${i}].userEmail`, value);
                                        //     form.setFieldValue(`watcher[${i}].userName`, selectedName);
                                        //   }}
                                        //   filterOption={(input, option: any) =>
                                        //     option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        //   }
                                        // >
                                        //   {approverData.map((email: any, index: any) => (
                                        //     <Select.Option
                                        //       key={index}
                                        //       value={email.email}
                                        //       label={email.name}
                                        //     >
                                        //       {email.email}
                                        //     </Select.Option>
                                        //   ))}
                                        // </Select>
                                        <Input
                                          disabled={viewItem ? true : false}
                                          {...field}
                                          style={{ width: "100%" }}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            form.setFieldValue(`watcher[${i}].userEmail`, value);
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`watcher[${i}].userEmail`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </Col>
                                {!viewItem && (
                                  <Col span={2}>
                                    <Form.Item label=" ">
                                      <Button
                                        type="link"
                                        danger
                                        disabled={props.values.watcher.length === 1}
                                        onClick={() => {
                                          const watcher = [...props.values.watcher];
                                          const removedWatcher = watcher.splice(i, 1)[0];

                                          // Remove from selected names
                                          setSelectedNames(
                                            selectedNames.filter((name) => name !== removedWatcher.userName)
                                          );

                                          props.setValues({
                                            ...props.values,
                                            watcher,
                                          });
                                        }}
                                      >
                                        <Icon
                                          icon="fluent:delete-16-regular"
                                          style={{
                                            color: styles.strawberry,
                                            cursor: "pointer",
                                            fontSize: 20,
                                          }}
                                        />
                                      </Button>
                                    </Form.Item>
                                  </Col>
                                )}
                              </Row>
                            ))}
                          </div>
                          {!viewItem && !id && (
                            <Row gutter={16}>
                              <Col
                                span={24}
                                style={{ textAlign: "right", marginBottom: 16 }}
                              >
                                <Button
                                  type="text"
                                  icon={<Icon icon="akar-icons:plus" />}
                                  disabled={props.values.watcher.some(
                                    (val) => val.userName === "" || val.userEmail === "" || id !== ""
                                  )}
                                  onClick={() =>
                                    props.setValues({
                                      ...props.values,
                                      watcher: [
                                        ...props.values.watcher,
                                        {
                                          userName: "",
                                          userEmail: "",
                                          designation: "R1",
                                          subsidiary: "",
                                          division: "",
                                          authorityGroup: authority,
                                        },
                                      ],
                                    })
                                  }
                                >
                                  Add Another
                                </Button>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </>
                    }
                    {!viewItem && (
                      <Row>
                        <Col
                          xs={24}
                          md={24}
                          style={{ textAlign: "right" }}
                        >
                          <Space>
                            <Button
                              htmlType="submit"
                              type="primary"
                              loading={isSubmitLoading}
                              disabled={!(props.isValid && props.dirty)}
                            >
                              Submit
                            </Button>
                          </Space>
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </ContentWrapper>
    </>
  );
};
